import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Drawer, Divider, Button,
  TextField, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup,
  CircularProgress, Grid, IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
  InputAdornment, MenuItem, Alert, Select, InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import ReplayIcon from '@mui/icons-material/Replay';
import { orderQualityCheckService, setupRequestService, defectReasonService } from '../services/api';
import BlockingScopeDialog from '../components/BlockingScopeDialog';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';

const COLUMNS = [
  { key: 'new', label: 'Новий', color: '#1565c0', bg: '#e3f2fd' },
  { key: 'repeat', label: 'Повторний', color: '#e65100', bg: '#fff3e0' },
  { key: 'done', label: 'Виконано', color: '#2e7d32', bg: '#e8f5e9' },
];

const RESULT_LABELS = {
  passed: 'Пройшов',
  temp_allowance: 'Тимчасовий допуск',
  failed: 'Не пройшов',
};

function fmtDate(val) {
  if (!val) return '—';
  try { return format(parseISO(val), 'dd.MM.yyyy HH:mm', { locale: uk }); }
  catch { return val; }
}

function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function QCCard({ item, colColor, colBg, onClick }) {
  return (
    <Paper
      elevation={2}
      onClick={() => onClick(item)}
      sx={{
        p: 1.5, mb: 1.5, cursor: 'pointer', borderLeft: `4px solid ${colColor}`,
        '&:hover': { boxShadow: 4 },
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" noWrap>
        {item.order_product_name || '—'}
      </Typography>
      {item.order_product_article && (
        <Typography variant="caption" color="text.secondary">{item.order_product_article}</Typography>
      )}
      <Typography variant="body2" mt={0.5}>
        Верстат: <b>{item.order_machine_identifier || '—'}</b>
      </Typography>
      {item.order_customer_order_number && (
        <Typography variant="body2">Замовлення: {item.order_customer_order_number}</Typography>
      )}
      <Typography variant="body2">
        Виготовлено: {item.order_produced_fact ?? 0} / {item.order_quantity ?? 0} шт
      </Typography>
      <Typography variant="body2">
        План до: {fmtDate(item.order_planned_end)}
      </Typography>
      {item.status === 'repeat' && item.checked_at && (
        <Typography variant="caption" color="warning.dark">
          Попередня перевірка: {fmtDate(item.checked_at)}
        </Typography>
      )}
      <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
        <Chip
          label={item.status_display || item.status}
          size="small"
          sx={{ bgcolor: colBg, color: colColor, fontWeight: 600 }}
        />
        {item.sequence > 1 && (
          <Chip label={`#${item.sequence}`} size="small" />
        )}
        {item.check_location === 'warehouse' && (
          <Chip label="СГП" size="small" color="secondary" />
        )}
        {item.result && (
          <Chip label={RESULT_LABELS[item.result] || item.result} size="small" variant="outlined" />
        )}
      </Box>
    </Paper>
  );
}

export default function QCKanbanPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = useMemo(() => {
    const h = searchParams.get('highlight');
    if (h != null && /^\d+$/.test(String(h).trim())) return Number(h.trim());
    return null;
  }, [searchParams]);
  const highlightOrderId = useMemo(() => {
    const o = searchParams.get('order');
    if (o != null && /^\d+$/.test(String(o).trim())) return Number(o.trim());
    return null;
  }, [searchParams]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [defectReasons, setDefectReasons] = useState([]);

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  })();

  const [callSetupSubmitting, setCallSetupSubmitting] = useState(false);
  const [recheckSubmitting, setRecheckSubmitting] = useState(false);

  const [result, setResult] = useState('passed');
  const [defectReasonId, setDefectReasonId] = useState('');
  const [retryAt, setRetryAt] = useState('');
  const [stopMachine, setStopMachine] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState('');
  const [blockDialog, setBlockDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orderQualityCheckService.getAll();
      setItems(res.data.results || res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    defectReasonService.getAll()
      .then((res) => setDefectReasons(res.data.results || res.data || []))
      .catch(() => setDefectReasons([]));
  }, []);

  function openDrawer(item) {
    setSelected(item);
    setResult('passed');
    setDefectReasonId('');
    const in2h = new Date(Date.now() + 2 * 60 * 60 * 1000);
    setRetryAt(toLocalInputValue(in2h));
    setStopMachine('');
    setPhotoFile(null);
    setComment('');
    setFormError('');
    setDrawerOpen(true);
  }

  useEffect(() => {
    if (loading || !items.length) return;
    if (highlightId == null && highlightOrderId == null) return;
    const hit = items.find((it) => (
      (highlightId != null && Number(it.id) === highlightId)
      || (highlightOrderId != null && Number(it.order) === highlightOrderId && it.status !== 'done')
    ));
    if (!hit) return;
    openDrawer(hit);
    const next = new URLSearchParams(searchParams);
    next.delete('highlight');
    next.delete('order');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, items, highlightId, highlightOrderId]);

  async function handleSubmit() {
    if (!selected) return;
    setFormError('');

    if (result === 'temp_allowance' && !retryAt) {
      setFormError('Вкажіть час повторної перевірки (не пізніше кінця зміни)');
      return;
    }
    if (result === 'failed') {
      if (!defectReasonId) {
        setFormError('Оберіть причину браку');
        return;
      }
      if (!comment.trim()) {
        setFormError('Коментар обовʼязковий');
        return;
      }
      if (stopMachine === '') {
        setFormError('Оберіть: зупинити ТПА або ТПА працює далі');
        return;
      }
      const reason = defectReasons.find((r) => String(r.id) === String(defectReasonId));
      if (reason?.comment_required && !comment.trim()) {
        setFormError('Для «ІН» коментар обовʼязковий');
        return;
      }
    }
    if (result === 'temp_allowance' && !comment.trim()) {
      setFormError('Коментар обовʼязковий для тимчасового допуску');
      return;
    }

    setSubmitting(true);
    try {
      let payload;
      if (photoFile) {
        const fd = new FormData();
        fd.append('result', result);
        fd.append('comment', comment.trim());
        fd.append('inspector_name', currentUser?.full_name || '');
        if (result === 'temp_allowance') {
          fd.append('retry_at', new Date(retryAt).toISOString());
        }
        if (result === 'failed') {
          fd.append('defect_reason', defectReasonId);
          fd.append('stop_machine', stopMachine === '1' ? 'true' : 'false');
          fd.append('photo', photoFile);
        }
        payload = fd;
      } else {
        payload = {
          result,
          comment: comment.trim(),
          inspector_name: currentUser?.full_name || '',
        };
        if (result === 'temp_allowance') {
          payload.retry_at = new Date(retryAt).toISOString();
        }
        if (result === 'failed') {
          payload.defect_reason = defectReasonId;
          payload.stop_machine = stopMachine === '1';
        }
      }
      const res = await orderQualityCheckService.complete(selected.id, payload);
      setDrawerOpen(false);
      load();
      if (res?.data?.blocking_pending || result === 'failed') {
        setBlockDialog({
          orderQualityCheckId: res?.data?.id || selected.id,
          orderId: selected.order,
        });
      }
    } catch (e) {
      const d = e?.response?.data;
      let msg = 'Помилка при збереженні';
      if (typeof d === 'string') msg = d;
      else if (d?.detail) msg = d.detail;
      else if (d && typeof d === 'object') {
        msg = Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
      }
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCallSetup() {
    if (!selected?.order) return;
    setCallSetupSubmitting(true);
    try {
      await setupRequestService.create({ order: selected.order });
      alert('✅ Заявку наладчику надіслано — очікуйте');
    } catch (e) {
      const raw = e?.response?.data?.detail || e?.response?.data?.order?.[0] || e?.response?.data?.non_field_errors?.[0] || '';
      const isExist = raw.toLowerCase().includes('вже існує') || raw.toLowerCase().includes('already');
      alert(isExist ? 'ℹ️ Заявка наладчику вже існує для цього замовлення' : (raw || e?.message || 'Помилка'));
    } finally {
      setCallSetupSubmitting(false);
    }
  }

  async function handleRequestRecheck() {
    if (!selected?.id) return;
    setRecheckSubmitting(true);
    try {
      const res = await orderQualityCheckService.requestRecheck(selected.id, {
        comment: selected.result === 'failed'
          ? 'Повтор після «Не пройшов»'
          : selected.result === 'temp_allowance'
            ? 'Повтор після тимчасового допуску'
            : 'Повторна перевірка',
      });
      const created = res?.data;
      await load();
      if (created?.id) {
        openDrawer(created);
      } else {
        setDrawerOpen(false);
      }
    } catch (e) {
      const d = e?.response?.data;
      alert(d?.detail || e?.message || 'Не вдалося створити повторну перевірку');
    } finally {
      setRecheckSubmitting(false);
    }
  }

  const filteredItems = items.filter(item => {
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (item.order_product_name || '').toLowerCase().includes(q) ||
        (item.order_product_article || '').toLowerCase().includes(q) ||
        (item.order_machine_identifier || '').toLowerCase().includes(q) ||
        (item.order_customer_order_number || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const byStatus = {};
  COLUMNS.forEach(c => { byStatus[c.key] = []; });
  filteredItems.forEach(i => { if (byStatus[i.status]) byStatus[i.status].push(i); });

  const visibleCols = statusFilter === 'all' ? COLUMNS : COLUMNS.filter(c => c.key === statusFilter);

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Пошук по виробу, верстату, замовленню..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => { if (v) setStatusFilter(v); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5, fontSize: 12 } }}
        >
          <ToggleButton value="all">Всі</ToggleButton>
          <ToggleButton value="new" sx={{ color: '#1565c0', '&.Mui-selected': { bgcolor: '#e3f2fd', color: '#1565c0' } }}>Новий</ToggleButton>
          <ToggleButton value="repeat" sx={{ color: '#e65100', '&.Mui-selected': { bgcolor: '#fff3e0', color: '#e65100' } }}>Повторний</ToggleButton>
          <ToggleButton value="done" sx={{ color: '#2e7d32', '&.Mui-selected': { bgcolor: '#e8f5e9', color: '#2e7d32' } }}>Виконано</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Оновити">
          <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={20} />}
      </Box>

      <Grid container spacing={2}>
        {visibleCols.map(col => (
          <Grid item xs={12} md={statusFilter === 'all' ? 4 : 12} key={col.key}>
            <Box
              sx={{
                bgcolor: col.bg, borderRadius: 2, p: 1.5,
                minHeight: 400,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography fontWeight="bold" color={col.color}>{col.label}</Typography>
                <Chip label={byStatus[col.key].length} size="small" sx={{ bgcolor: col.color, color: '#fff' }} />
              </Box>
              {byStatus[col.key].length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" mt={4}>
                  Немає записів
                </Typography>
              )}
              {byStatus[col.key].map(item => (
                <QCCard
                  key={item.id}
                  item={item}
                  colColor={col.color}
                  colBg={col.bg}
                  onClick={openDrawer}
                />
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 440 }, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">ВКЯ #{selected?.sequence}</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          {selected && (
            <>
              <Typography variant="subtitle1" fontWeight="bold">{selected.order_product_name}</Typography>
              {selected.order_product_article && (
                <Typography variant="body2" color="text.secondary">{selected.order_product_article}</Typography>
              )}
              <Typography variant="body2">Верстат: {selected.order_machine_identifier} — {selected.order_machine_name}</Typography>
              <Typography variant="body2">Замовлення: {selected.order_customer_order_number || '—'}</Typography>
              <Typography variant="body2">
                Виготовлено: {selected.order_produced_fact ?? 0} / {selected.order_quantity ?? 0} шт
              </Typography>
              <Typography variant="body2">Запущено: {fmtDate(selected.order_launched_at)}</Typography>
              <Typography variant="body2">План до: {fmtDate(selected.order_planned_end)}</Typography>

              {selected.check_location === 'warehouse' && (
                <Box mt={1} p={1} sx={{ bgcolor: '#ede7f6', borderRadius: 1, border: '1px solid #b39ddb' }}>
                  <Typography variant="body2" fontWeight={700} color="#4527a0">
                    Перевірка на складі готової продукції
                  </Typography>
                </Box>
              )}

              <Box mt={1.5} p={1.5} sx={{ bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                  Потрібна наладка?
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={callSetupSubmitting ? <CircularProgress size={14} /> : <BuildIcon sx={{ fontSize: 16 }} />}
                  disabled={callSetupSubmitting}
                  onClick={handleCallSetup}
                  sx={{ textTransform: 'none', fontWeight: 700, borderColor: '#f57c00', color: '#f57c00',
                    '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' } }}
                >
                  Викликати наладчика
                </Button>
              </Box>

              {selected.status === 'done' ? (
                <Box mt={2}>
                  <Chip label="Перевірку завершено" color="success" />
                  <Typography mt={1}>
                    Результат: {RESULT_LABELS[selected.result] || (selected.passed ? 'Пройдено' : 'Не пройдено')}
                  </Typography>
                  {selected.defect_reason_label && (
                    <Typography>Причина: {selected.defect_reason_label}</Typography>
                  )}
                  {selected.retry_at && (
                    <Typography>Повторна: {fmtDate(selected.retry_at)}</Typography>
                  )}
                  {selected.stop_machine != null && (
                    <Typography>
                      ТПА: {selected.stop_machine ? 'зупинено' : 'працює далі'}
                    </Typography>
                  )}
                  {selected.blocking_required && (
                    <Typography color="error" mb={1}>Потрібне блокування продукції</Typography>
                  )}
                  {selected.blocking_required && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 700, mb: 1.5 }}
                      onClick={() => setBlockDialog({
                        orderQualityCheckId: selected.id,
                        orderId: selected.order,
                      })}
                    >
                      Заблокувати продукцію
                    </Button>
                  )}
                  <Typography>Контролер: {selected.inspector_name || '—'}</Typography>
                  <Typography>Коментар: {selected.comment || '—'}</Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    startIcon={recheckSubmitting ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
                    disabled={recheckSubmitting}
                    onClick={handleRequestRecheck}
                    sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Повторна перевірка
                  </Button>
                </Box>
              ) : (
                <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
                  <Divider>Результат ВКЯ</Divider>
                  {formError && <Alert severity="error">{formError}</Alert>}

                  <FormControl>
                    <FormLabel>Результат</FormLabel>
                    <RadioGroup
                      value={result}
                      onChange={(e) => setResult(e.target.value)}
                    >
                      <FormControlLabel value="passed" control={<Radio />} label="Пройшов" />
                      <FormControlLabel value="temp_allowance" control={<Radio />} label="Тимчасовий допуск" />
                      <FormControlLabel value="failed" control={<Radio />} label="Не пройшов" />
                    </RadioGroup>
                  </FormControl>

                  {result === 'temp_allowance' && (
                    <>
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Відвантаження ГП цього ВЗ буде заблоковане до окремого погодження ВКЯ на складі блокування.
                      </Alert>
                      <TextField
                        label="Повторна перевірка до"
                        type="datetime-local"
                        size="small"
                        value={retryAt}
                        onChange={(e) => setRetryAt(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Не пізніше кінця поточної зміни"
                        fullWidth
                      />
                    </>
                  )}

                  {result === 'failed' && (
                    <>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="defect-reason-label">Причина браку</InputLabel>
                        <Select
                          labelId="defect-reason-label"
                          label="Причина браку"
                          value={defectReasonId}
                          onChange={(e) => setDefectReasonId(e.target.value)}
                        >
                          {defectReasons.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {r.code} — {r.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Рішення по ТПА</FormLabel>
                        <RadioGroup
                          value={stopMachine}
                          onChange={(e) => setStopMachine(e.target.value)}
                        >
                          <FormControlLabel value="1" control={<Radio />} label="Зупинити ТПА" />
                          <FormControlLabel value="0" control={<Radio />} label="ТПА працює далі" />
                        </RadioGroup>
                      </FormControl>

                      <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                        {photoFile ? `Фото: ${photoFile.name}` : 'Фото (необовʼязково)'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                        />
                      </Button>
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        Продукцію буде потрібно заблокувати (обсяг — блок 4).
                      </Alert>
                    </>
                  )}

                  <TextField
                    label={result === 'passed' ? 'Коментар' : 'Коментар (обовʼязковий)'}
                    multiline
                    rows={2}
                    size="small"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                  />
                  {currentUser?.full_name && (
                    <Typography variant="caption" color="text.secondary">
                      Контролер: <b>{currentUser.full_name}</b>
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? <CircularProgress size={20} /> : 'Зафіксувати результат'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <BlockingScopeDialog
        open={!!blockDialog}
        orderQualityCheckId={blockDialog?.orderQualityCheckId}
        orderId={blockDialog?.orderId}
        onClose={() => setBlockDialog(null)}
        onDone={() => {
          setBlockDialog(null);
          load();
          alert('Продукцію заблоковано на складі блокування.');
        }}
      />
    </Box>
  );
}
